import { Box, Stack, Typography } from "@mui/material";
import { useLatestPayPointTransaction } from "Api/Queries/Transactions/useLatestPayPointTransaction";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import type { InitiateTransactionRequest } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { useReplaceExistingTransactionMutation } from "Hooks/useReplaceExistingTransactionMutation";
import { formatCurrency } from "Utils/CurrencyUtils";

const COMPONENT_RESOURCES =
  Resources.Merchant.Initiate.ActiveTransactionConflict;

type Props = {
  onCancel: () => void;
  pendingTransaction: InitiateTransactionRequest | null;
};

export const ActiveTransactionConflictModal = ({
  onCancel,
  pendingTransaction,
}: Props) => {
  const { t } = useResource();

  const {
    data: latestPayPointTransaction,
    isLoading: isLoadingLatestPayPointTransaction,
  } = useLatestPayPointTransaction(pendingTransaction?.payPointPublicID);

  const { mutate: replaceTransaction, isPending } =
    useReplaceExistingTransactionMutation();

  if (isLoadingLatestPayPointTransaction) {
    return <CenteredCircularProgress />;
  }

  if (!latestPayPointTransaction) {
    onCancel();
    return null;
  }

  return (
    <ModalDialog isOpen onClose={onCancel} title={t(COMPONENT_RESOURCES.Title)}>
      <Stack gap={3}>
        <Typography variant="body1">
          {t(COMPONENT_RESOURCES.Message)}
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {`${latestPayPointTransaction.publicID} | ${formatCurrency({
            amount: latestPayPointTransaction.amount,
            currencyCode: latestPayPointTransaction.currencyCode,
          })}`}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t(COMPONENT_RESOURCES.Description)}
        </Typography>
      </Stack>
      <Box
        display="flex"
        flexDirection={["column", "column", "row"]}
        justifyContent="space-between"
        gap={2}
      >
        <PrimaryButton
          size="small"
          variant="text"
          color="error"
          disabled={isPending}
          onClick={
            latestPayPointTransaction.publicID && pendingTransaction
              ? () =>
                  replaceTransaction(
                    latestPayPointTransaction.publicID,
                    pendingTransaction,
                  )
              : undefined
          }
        >
          {isPending
            ? t(COMPONENT_RESOURCES.Overwriting)
            : t(COMPONENT_RESOURCES.OverwriteButton)}
        </PrimaryButton>
        <PrimaryButton
          onClick={onCancel}
          color="info"
          size="small"
          startIcon={<KeyboardArrowLeft />}
        >
          {t(Resources.Common.Back)}
        </PrimaryButton>
      </Box>
    </ModalDialog>
  );
};
